import { useState } from 'react';
import useSWR from 'swr';
import { toast } from 'App';
import {
  CreatePermissionGate,
  DeletePermissionGate,
  UpdatePermissionGate,
} from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { adminService } from 'views/services/adminService';
import { invoicePaymentsService } from 'views/services/invoicePaymentsService';
import { IInvoiceCountry } from 'views/types/invoicePayments';
import {
  cellClass,
  dangerSmallBtn,
  emptyToNull,
  headerClass,
  inputCls,
  labelCls,
  primaryBtn,
  secondaryBtn,
  smallBtn,
} from './shared';

interface IDraft {
  name: string;
  iso_code: string;
  list_type: 'applicant' | 'beneficiary';
  status: 'supported' | 'restricted' | 'disabled';
  enhanced_review: boolean;
}

const EMPTY: IDraft = {
  name: '',
  iso_code: '',
  list_type: 'beneficiary',
  status: 'supported',
  enhanced_review: false,
};

export const CountriesPanel = () => {
  const [listType, setListType] = useState('');
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<IDraft>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: countries, mutate } = useSWR(
    ['/api/admin/invoice-payment-config/countries', listType, search],
    () =>
      invoicePaymentsService.getCountries({
        listType: listType || undefined,
        search: search || undefined,
      })
  );

  const startEdit = (country: IInvoiceCountry) => {
    setEditingId(country.id);
    setDraft({
      name: country.name,
      iso_code: country.iso_code ?? '',
      list_type: country.list_type,
      status: country.status,
      enhanced_review: country.enhanced_review,
    });
  };

  const submit = async () => {
    if (!draft.name.trim()) {
      toast({ title: 'Name is required', status: 'error' });
      return;
    }
    const payload = {
      name: draft.name.trim(),
      iso_code: emptyToNull(draft.iso_code.toUpperCase()),
      list_type: draft.list_type,
      status: draft.status,
      enhanced_review: draft.enhanced_review,
    };
    try {
      setBusy(true);
      if (editingId) {
        await invoicePaymentsService.updateCountry(editingId, payload);
        toast({ title: 'Country updated', status: 'success' });
      } else {
        await invoicePaymentsService.createCountry(payload);
        toast({ title: 'Country created', status: 'success' });
      }
      setDraft(EMPTY);
      setEditingId(null);
      await mutate();
    } catch (error) {
      adminService.errorHandler(error);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this country entry?')) return;
    try {
      setBusy(true);
      await invoicePaymentsService.deleteCountry(id);
      toast({ title: 'Country deleted', status: 'success' });
      await mutate();
    } catch (error) {
      adminService.errorHandler(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-3 text-xs text-gray-500">
        Applicant list = who may use Invoice Payment (ToU Schedule 1).
        Beneficiary list = where payments may go (Schedule 2). A country missing
        from a list is treated as allowed; add it with status Disabled to block
        it.
      </div>

      <CreatePermissionGate resource={PERMISSION_RESOURCES.INVOICE_CONFIG}>
        <div className="mb-4 grid grid-cols-2 gap-3 rounded border border-gray-200 p-3 dark:border-white/20 md:grid-cols-6">
          <div>
            <label className={labelCls}>Name</label>
            <input
              className={inputCls}
              value={draft.name}
              onChange={e => setDraft({ ...draft, name: e.target.value })}
            />
          </div>
          <div>
            <label className={labelCls}>ISO-2 (empty for regions)</label>
            <input
              className={inputCls}
              maxLength={2}
              value={draft.iso_code}
              onChange={e => setDraft({ ...draft, iso_code: e.target.value })}
            />
          </div>
          <div>
            <label className={labelCls}>List</label>
            <select
              className={inputCls}
              value={draft.list_type}
              onChange={e =>
                setDraft({
                  ...draft,
                  list_type: e.target.value as IDraft['list_type'],
                })
              }
            >
              <option value="applicant">Applicant</option>
              <option value="beneficiary">Beneficiary</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select
              className={inputCls}
              value={draft.status}
              onChange={e =>
                setDraft({
                  ...draft,
                  status: e.target.value as IDraft['status'],
                })
              }
            >
              <option value="supported">Supported</option>
              <option value="restricted">Restricted</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm dark:text-white">
              <input
                type="checkbox"
                checked={draft.enhanced_review}
                onChange={e =>
                  setDraft({ ...draft, enhanced_review: e.target.checked })
                }
              />
              Enhanced review
            </label>
          </div>
          <div className="flex items-end gap-2">
            <button disabled={busy} onClick={submit} className={primaryBtn}>
              {editingId ? 'Save' : 'Add'}
            </button>
            {editingId && (
              <button
                className={secondaryBtn}
                onClick={() => {
                  setEditingId(null);
                  setDraft(EMPTY);
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </CreatePermissionGate>

      <div className="mb-3 flex gap-3">
        <select
          className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:bg-navy-800 dark:text-white"
          value={listType}
          onChange={e => setListType(e.target.value)}
        >
          <option value="">Both lists</option>
          <option value="applicant">Applicant</option>
          <option value="beneficiary">Beneficiary</option>
        </select>
        <input
          className="w-64 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:bg-navy-800 dark:text-white"
          placeholder="Search name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <span className="self-center text-sm text-gray-500">
          {countries?.length ?? 0} entries
        </span>
      </div>

      <div className="max-h-[520px] overflow-y-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className={headerClass}>Name</th>
              <th className={headerClass}>ISO</th>
              <th className={headerClass}>List</th>
              <th className={headerClass}>Status</th>
              <th className={headerClass}>Enhanced review</th>
              <th className={headerClass}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {countries?.map(country => (
              <tr key={country.id}>
                <td className={cellClass}>{country.name}</td>
                <td className={cellClass}>{country.iso_code ?? '—'}</td>
                <td className={cellClass}>{country.list_type}</td>
                <td className={cellClass}>
                  <span
                    className={
                      country.status === 'supported'
                        ? 'text-green-600'
                        : country.status === 'disabled'
                          ? 'text-red-500'
                          : 'text-amber-500'
                    }
                  >
                    {country.status}
                  </span>
                </td>
                <td className={cellClass}>
                  {country.enhanced_review ? 'Yes' : '—'}
                </td>
                <td className={`${cellClass} flex gap-2`}>
                  <UpdatePermissionGate
                    resource={PERMISSION_RESOURCES.INVOICE_CONFIG}
                  >
                    <button
                      className={smallBtn}
                      onClick={() => startEdit(country)}
                    >
                      Edit
                    </button>
                  </UpdatePermissionGate>
                  <DeletePermissionGate
                    resource={PERMISSION_RESOURCES.INVOICE_CONFIG}
                  >
                    <button
                      className={dangerSmallBtn}
                      disabled={busy}
                      onClick={() => remove(country.id)}
                    >
                      Delete
                    </button>
                  </DeletePermissionGate>
                </td>
              </tr>
            ))}
            {countries && countries.length === 0 && (
              <tr>
                <td
                  className="py-4 text-center text-sm text-gray-500"
                  colSpan={6}
                >
                  No countries found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
